<template>
  <ScrollableTable class="us-board-table" v-loading="loading" element-loading-background="var(--theme-bg)">
    <table :style="{ minWidth: Math.max(720, (columns.length + 1) * 120) + 'px' }">
      <thead><tr><th class="sequence">序号</th><th v-for="column in columns" :key="column.key">{{ column.title }}</th></tr></thead>
      <tbody>
        <tr v-for="(item, index) in rows" :key="item.full_code" tabindex="0" @click="$emit('select-stock', item)" @keydown.enter="$emit('select-stock', item)">
          <td>{{ index + 1 }}</td>
          <td v-for="column in columns" :key="column.key" :class="valueClass(item, column)" :title="format(item, column)">{{ format(item, column) }}</td>
        </tr>
      </tbody>
    </table>
    <div v-if="!loading && !rows.length" class="table-state">暂无板块行情</div>
    <div v-if="error" class="table-state" role="status">{{ error }} <button type="button" @click="activate">重试</button></div>
  </ScrollableTable>
</template>
<script>
import { getStockQuotes } from '@/utils/stockQuotes'
import { sharedStockRow } from '@/utils/stockQuoteFeed'
import { pollMarket } from '@/utils/marketRefresh'
import { getUSOverview, normalizeUSQuote } from '@/utils/usQuotes'
import { quoteNumber, quoteDisplay, quotePrice } from '@/utils/quoteDisplay'
import { stockSelection } from '@/views/stock/stockSelection'
import ScrollableTable from '@/components/ScrollableTable.vue'

export default {
  name: 'USBoardTable',
  components: { ScrollableTable },
  props: { category: { type: String, required: true }, active: { type: Boolean, default: true } },
  data: () => ({ stocks: [], loaded: false, quotes: {}, loading: false, error: '', sequence: 0,
    columns: [
      { key: 'code', title: '代码' }, { key: 'name', title: '名称' },
      { key: 'price', title: '最新价' }, { key: 'delta', title: '涨跌额', change: true },
      { key: 'change', title: '涨跌幅', percent: true, change: true },
      { key: 'amount', title: '成交额（万美元）', divisor: 10000, digits: 2 }, { key: 'volume', title: '成交量（万）', divisor: 10000, digits: 2 },
      { key: 'amplitude', title: '振幅(%)', percent: true }, { key: 'turnover', title: '换手率', percent: true },
      { key: 'pe', title: '市盈率(TTM)' }, { key: 'pb', title: '市净率' }, { key: 'ratio', title: '量比' },
      { key: 'marketValue', title: '总市值（亿美元）', digits: 2 }, { key: 'floatValue', title: '流通市值（亿美元）', digits: 2 }
    ] }),
  computed: {
    rows() {
      return this.stocks.map(item => {
        const code = stockSelection({ ...item, market: 'us' }).code
        const detail = this.quotes[code] || {}
        const value = (source, fallback) => quoteNumber(source) == null ? fallback : source
        return sharedStockRow({ ...item, ...detail, code, name: item.prod_name || item.name,
          price: value(detail.price, item.last_px), delta: value(detail.delta, item.px_change),
          change: value(detail.change, item.px_change_rate), amount: value(detail.amount, item.amount),
          turnover: value(detail.turnover, item.exchange), amplitude: value(detail.amplitude, item.amplitude), ratio: value(detail.ratio, item.volratio) }, 'us')
      })
    }
  },
  watch: { active(value) { if (value) this.activate(); else this.stopPolling() } },
  mounted() { if (this.active) this.activate() },
  beforeDestroy() { this.disposed = true; this.sequence++; this.stopPolling() },
  methods: {
    stopPolling() { if (this.stopRefresh) this.stopRefresh(); this.stopRefresh = null },
    async activate() {
      if (this.disposed || !this.active) return
      try {
        if (!this.loaded || !this.stocks.length) await this.loadStocks()
        await this.load()
      } finally {
        if (!this.disposed && this.active && !this.stopRefresh) this.stopRefresh = pollMarket(() => this.load(), 'us')
      }
    },
    async loadStocks() {
      if (this.disposed || !this.active || this.loading) return
      this.loading = true; this.error = ''
      try {
        const result = await getUSOverview()
        if (this.disposed) return
        const rows = result && result.ranks && result.ranks[this.category + '-advance']
        if (!Array.isArray(rows)) throw new Error('Invalid US board')
        this.stocks = rows; this.loaded = true
      } catch (_) { this.error = '美股板块列表暂时无法加载，请重试。' }
      finally { this.loading = false }
    },
    async load() {
      if (this.disposed || !this.active || this.loading || !this.stocks.length) return
      this.loading = true
      const sequence = ++this.sequence
      this.error = ''
      const codes = [...new Set(this.stocks.map(item => stockSelection({ ...item, market: 'us' }).code))]
      const updates = {}
      await Promise.all(Array.from({ length: Math.ceil(codes.length / 100) }, async (_, index) => {
        const batch = codes.slice(index * 100, index * 100 + 100)
        try {
          const result = await getStockQuotes(batch.join(','))
          for (const code of batch) {
            try {
              const detail = { ...normalizeUSQuote(result[code]), delta: result[code][31], ratio: result[code][49] }
              updates[code] = { ...this.quotes[code], ...Object.fromEntries(Object.entries(detail).filter(([key, value]) => key === 'name' ? !!value : quoteNumber(value) != null)) }
            }
            catch (_) { /* Keep cached fields; missing values render as --. */ }
          }
        } catch (_) { /* Retry quotes on the next scheduled refresh. */ }
      }))
      if (sequence !== this.sequence) return
      this.quotes = { ...this.quotes, ...updates }
      this.loading = false
    },
    format(item, column) {
      if (column.key === 'code') return item.code.replace(/^us/i, '')
      if (column.key === 'name') return item.name || '--'
      if (column.key === 'price' || column.key === 'delta') return quotePrice(item[column.key])
      if (column.divisor || column.digits != null) return quoteDisplay(item[column.key], '', column.divisor || 1, column.digits)
      return quoteDisplay(item[column.key], column.percent ? '%' : '')
    },
    valueClass(item, column) {
      if (!column.change) return ''
      const value = quoteNumber(item[column.key])
      return value > 0 ? 'rise' : value < 0 ? 'fall' : ''
    }
  }
}
</script>
<style scoped>
.us-board-table { flex: 1; min-height: 0; overflow: hidden; background: var(--theme-bg); color: var(--theme-label); font-size: 14px; }
table { width: 100%; table-layout: fixed; border-collapse: collapse; text-align: center; }
th, td { height: 31px; padding: 0 6px; font-weight: normal; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
th { position: sticky; top: 0; z-index: 1; background: var(--theme-header); }
.sequence { width: 50px; }
tbody tr { cursor: pointer; } tbody tr:hover { background: var(--theme-panel); }
.rise { color: var(--quote-up); } .fall { color: var(--quote-down); }
.table-state { padding: 12px; text-align: center; }
button { background: transparent; color: var(--theme-label); border: 1px solid var(--theme-label); cursor: pointer; }
</style>
